import React, { useContext, useState, useEffect } from "react";
import { DataContext } from "../contexts/DataContext";
import { useAuth } from "../contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import logo from "../logo-wis.avif";
import GlobalHeader from "./GlobalHeader";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faFilter,
  faSpinner,
  faFileExcel,
  faCalendarAlt,
  faTimes,
  faChevronDown,
  faChevronUp,
  faChartBar,
  faChartLine,
  faChartPie,
  faTable,
  faSearch,
  faSync,
  faInfoCircle,
  faShip,
  faTruck,
  faPlane,
  faQuestion,
  faTag,
  faBuilding,
  faMapMarkerAlt,
  faIdCard,
  faCog,
  faFileInvoice,
  faClipboardCheck,
  faColumns,
  faExpand,
  faCompress,
  faHome,
  faLayerGroup,
  faCubes,
  faDownload,
  faArrowRight,
  faProcedures,
  faQuestionCircle,
  faBell,
  faEnvelope,
  faUser,
  faSignOutAlt,
} from "@fortawesome/free-solid-svg-icons";
import { DateRangePicker } from "react-date-range";
import "react-date-range/dist/styles.css";
import "react-date-range/dist/theme/default.css";
import Select from "react-select";

// Import chart components
import ReferencesPerEntryPointBarChart from "./ReferencesPerEntryPointBarChart";
import EnhancedReferencesPerDocumentTypeBarChart from "./EnhancedReferencesPerDocumentTypeBarChart";
import EnhancedReferencesPerStatusBarChart from "./EnhancedReferencesPerStatusBarChart";
import EnhancedReferencesPerOperationTypeDonutChart from "./EnhancedReferencesPerOperationTypeDonutChart";
import EnhancedReferencesPerEntryPointAndLicenseBarChart from "./EnhancedReferencesPerEntryPointAndLicenseBarChart";
import ReferencesDistributionByCarrierBarChart from "./ReferencesDistributionByCarrierBarChart";
import EnhancedAttentionTimeBarChart from "./EnhancedAttentionTimeBarChart";
import DocTimeBarChart from "./DocTimeBarChart";
import EnhancedAttentionDocumentTimesSeriesChart from "./EnhancedAttentionDocumentTimesSeriesChart";
import EnhancedReferencesPerEntryPointBarChart from "./EnhancedReferencesPerEntryPointBarChart";
import ProcessingEfficiencyMatrixChart from "./ProcessingEfficiencyMatrixChart";
import ProcessBottleneckAnalyzer from "./ProcessBottleneckAnalyzer";
import DrillDownModal from "./DrillDownModal";

const EnhancedDashboard = () => {
  const { trafficData, isLoading, fetchTrafficData, setShouldFetchData } =
    useContext(DataContext);
  const { logout } = useAuth();
  const navigate = useNavigate();

  // State for managing filtered data
  const [filteredData, setFilteredData] = useState([]);

  // State for layout and UI control
  const [activeTab, setActiveTab] = useState("overview");
  const [selectedChart, setSelectedChart] = useState(null);
  const [detailsItem, setDetailsItem] = useState(null);
  const [showDetailsPanel, setShowDetailsPanel] = useState(false);
  const [expandedView, setExpandedView] = useState(false);

  // State for drill-down modal
  const [showDrillDownModal, setShowDrillDownModal] = useState(false);
  const [drillDownConfig, setDrillDownConfig] = useState(null);

  // State for filters
  const [showFilters, setShowFilters] = useState(false);
  const [showDatePicker, setShowDatePicker] = useState(false);
  
  // State for menu dropdowns
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [dateRange, setDateRange] = useState([
    {
      startDate: null,
      endDate: null,
      key: "selection",
    },
  ]);

  // Filter states
  const [selectedCustomers, setSelectedCustomers] = useState([]);
  const [selectedDivisions, setSelectedDivisions] = useState([]);
  const [selectedRfcs, setSelectedRfcs] = useState([]);
  const [selectedOperationTypes, setSelectedOperationTypes] = useState([]);
  const [selectedEntryPoints, setSelectedEntryPoints] = useState([]);
  const [selectedPedimentos, setSelectedPedimentos] = useState([]);
  const [selectedStatus, setSelectedStatus] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");

  // Filter options
  const [customerOptions, setCustomerOptions] = useState([]);
  const [divisionOptions, setDivisionOptions] = useState([]);
  const [rfcOptions, setRfcOptions] = useState([]);
  const [operationTypeOptions, setOperationTypeOptions] = useState([]);
  const [entryPointOptions, setEntryPointOptions] = useState([]);
  const [pedimentoOptions, setPedimentoOptions] = useState([]);
  const statusOptions = [
    { value: "Paid", label: "Paid" },
    { value: "Open", label: "Open" },
  ];

  // Grid and pagination state
  const [sortConfig, setSortConfig] = useState({
    key: "detail.dates_data.date_init",
    direction: "desc",
  });
  const [pagination, setPagination] = useState({
    currentPage: 1,
    pageSize: 10,
    totalPages: 1,
    totalItems: 0,
  });

  // Chart display configuration
  const [chartLayout, setChartLayout] = useState({
    columns: 2, // Default to 2 columns
    size: "medium", // Options: small, medium, large
  });

  // Define available charts
  const availableCharts = [
    {
      id: "entryPoint",
      title: "References per Entry Point",
      component: EnhancedReferencesPerEntryPointBarChart,
      icon: faShip,
    },
    {
      id: "documentType",
      title: "References per Document Type",
      component: EnhancedReferencesPerDocumentTypeBarChart,
      icon: faFileInvoice,
    },
    {
      id: "status",
      title: "References per Status",
      component: EnhancedReferencesPerStatusBarChart,
      icon: faTag,
    },
    {
      id: "operationType",
      title: "References per Operation Type",
      component: EnhancedReferencesPerOperationTypeDonutChart,
      icon: faCog,
    },
    {
      id: "entryPointLicense",
      title: "References per Entry Point & License",
      component: EnhancedReferencesPerEntryPointAndLicenseBarChart,
      icon: faIdCard,
    },
    {
      id: "carrier",
      title: "Distribution by Carrier",
      component: ReferencesDistributionByCarrierBarChart,
      icon: faTruck,
    },
    {
      id: "attentionTime",
      title: "Attention Time Analysis",
      component: EnhancedAttentionTimeBarChart,
      icon: faChartBar,
    },
    {
      id: "docTime",
      title: "Document Processing Time",
      component: DocTimeBarChart,
      icon: faChartLine,
    },
    {
      id: "timeSeries",
      title: "Time Series Analysis",
      component: EnhancedAttentionDocumentTimesSeriesChart,
      icon: faChartLine,
    },
    {
      id: "processingEfficiency",
      title: "Processing Efficiency",
      component: ProcessingEfficiencyMatrixChart,
      icon: faProcedures,
    },
    {
      id: "processBottleneckAnalyzer",
      title: "Process Bottleneck Analyzer",
      component: ProcessBottleneckAnalyzer,
      icon: faProcedures,
    },
  ];

  // Selected charts for dashboard display
  const [selectedCharts, setSelectedCharts] = useState([
    "entryPoint",
    "documentType",
    "status",
    "operationType",
    "entryPointLicense",
    "attentionTime",
    "timeSeries",
    "processingEfficiency",
    "processBottleneckAnalyzer",
  ]);

  // Fetch data when component mounts
  useEffect(() => {
    fetchTrafficData();
  }, [fetchTrafficData]);

  // Process traffic data to extract filter options
  useEffect(() => {
    if (trafficData && trafficData.length > 0) {
      // Extract filter options from data
      const uniqueCustomers = Array.from(
        new Set(trafficData.map((item) => item.header.customer_name))
      )
        .filter(Boolean)
        .map((customer) => ({ value: customer, label: customer }));
      setCustomerOptions(uniqueCustomers);

      const uniqueDivisions = Array.from(
        new Set(trafficData.map((item) => item.header.customer_division))
      )
        .filter(Boolean)
        .map((division) => ({ value: division, label: division }));
      setDivisionOptions(uniqueDivisions);

      const uniqueRfcs = Array.from(
        new Set(trafficData.map((item) => item.header.customer_rfc))
      )
        .filter(Boolean)
        .map((rfc) => ({ value: rfc, label: rfc }));
      setRfcOptions(uniqueRfcs);

      const uniqueOperationTypes = Array.from(
        new Set(trafficData.map((item) => item.header.operation_type))
      )
        .filter(Boolean)
        .map((operationType) => ({
          value: operationType,
          label: getOperationTypeText(operationType),
        }));
      setOperationTypeOptions(uniqueOperationTypes);

      const uniqueEntryPoints = Array.from(
        new Set(
          trafficData
            .map((item) => item.header.pedimento?.pedimento_entry_point)
            .filter(Boolean)
        )
      ).map((entryPoint) => ({ value: entryPoint, label: entryPoint }));
      setEntryPointOptions(uniqueEntryPoints);

      const uniquePedimentos = Array.from(
        new Set(
          trafficData
            .map((item) => item.header.pedimento?.pedimento_number)
            .filter(Boolean)
        )
      ).map((pedimento) => ({ value: pedimento, label: pedimento }));
      setPedimentoOptions(uniquePedimentos);

      // Set filtered data to all data initially
      setFilteredData(trafficData);
    }
  }, [trafficData]);

  // Apply filters to data
  useEffect(() => {
    if (!trafficData) return;

    let filtered = trafficData;

    // Apply search term filter
    if (searchTerm) {
      filtered = filtered.filter((item) => {
        const checkObjectForMatch = (obj, term) => {
          if (!obj) return false;

          for (const key in obj) {
            const value = obj[key];

            if (value && typeof value === "object" && !Array.isArray(value)) {
              if (checkObjectForMatch(value, term)) {
                return true;
              }
            } else if (String(value).toLowerCase().includes(term)) {
              return true;
            }
          }

          return false;
        };

        return checkObjectForMatch(item.header, searchTerm.toLowerCase());
      });
    }

    // Apply dropdown filters
    if (selectedCustomers.length) {
      filtered = filtered.filter((item) =>
        selectedCustomers.some(
          (selected) => selected.value === item.header.customer_name
        )
      );
    }

    if (selectedDivisions.length) {
      filtered = filtered.filter((item) =>
        selectedDivisions.some(
          (selected) => selected.value === item.header.customer_division
        )
      );
    }

    if (selectedRfcs.length) {
      filtered = filtered.filter((item) =>
        selectedRfcs.some(
          (selected) => selected.value === item.header.customer_rfc
        )
      );
    }

    if (selectedOperationTypes.length) {
      filtered = filtered.filter((item) =>
        selectedOperationTypes.some(
          (selected) => selected.value === item.header.operation_type
        )
      );
    }

    if (selectedEntryPoints.length) {
      filtered = filtered.filter((item) =>
        selectedEntryPoints.some(
          (selected) =>
            selected.value === item.header.pedimento.pedimento_entry_point
        )
      );
    }

    if (selectedPedimentos.length) {
      filtered = filtered.filter((item) =>
        selectedPedimentos.some(
          (selected) =>
            selected.value === item.header.pedimento.pedimento_number
        )
      );
    }

    if (selectedStatus.length) {
      filtered = filtered.filter((item) => {
        const status =
          item.header.detail?.traffic_data?.status === "Pagado"
            ? "Paid"
            : "Open";
        return selectedStatus.some((selected) => selected.value === status);
      });
    }

    // Replace the existing getChartSizeClass function
    const getChartSizeClass = () => {
      const columnClasses = {
        1: "grid-cols-1",
        2: "grid-cols-1 lg:grid-cols-2",
        3: "grid-cols-1 md:grid-cols-2 xl:grid-cols-3",
      };

      return {
        columns: columnClasses[chartLayout.columns],
      };
    };

    // Apply date range filter
    const { startDate, endDate } = dateRange[0];
    if (startDate && endDate) {
      filtered = filtered.filter((item) => {
        const dateInit = item.header.detail?.dates_data?.date_init
          ? new Date(item.header.detail.dates_data.date_init)
          : null;
        return dateInit && dateInit >= startDate && dateInit <= endDate;
      });
    }

    setFilteredData(filtered);

    // Update pagination
    const totalPages = Math.ceil(filtered.length / pagination.pageSize);
    setPagination((prev) => ({
      ...prev,
      totalPages: totalPages,
      totalItems: filtered.length,
      currentPage: 1, // Reset to first page when filters change
    }));
  }, [
    trafficData,
    searchTerm,
    selectedCustomers,
    selectedDivisions,
    selectedRfcs,
    selectedOperationTypes,
    selectedEntryPoints,
    selectedPedimentos,
    selectedStatus,
    dateRange,
    pagination.pageSize,
  ]);

  // Trigger data fetch when component mounts
  useEffect(() => {
    // Correctly update the shouldFetchData object
    setShouldFetchData((prev) => {
      // Make sure you're dealing with an object
      const prevObject = typeof prev === "object" ? prev : {};
      return {
        ...prevObject,
        fetchTrafficData: true,
      };
    });

    return () => {
      setShouldFetchData((prev) => {
        const prevObject = typeof prev === "object" ? prev : {};
        return {
          ...prevObject,
          fetchTrafficData: false,
        };
      });
    };
  }, [setShouldFetchData]);

  const handleRefreshData = () => {
    setShouldFetchData((prev) => ({
      ...prev,
      fetchTrafficData: true,
    }));
    fetchTrafficData();
  };

  // Handle logout functionality
  const handleLogout = () => {
    logout();
    navigate("/");
  };

  // Fetch data when component mounts
  useEffect(() => {
    fetchTrafficData();
  }, [fetchTrafficData]);

  // Helper functions
  const getOperationTypeText = (operationType) => {
    if (operationType === "1") return "Import";
    if (operationType === "2") return "Export";
    return operationType;
  };

  const getTransportIcon = (entryPoint) => {
    if (!entryPoint)
      return {
        icon: faQuestion,
        color: "text-gray-500",
        title: "Unknown Transport Type",
      };

    const transportIconsMap = {
      // Seafreight
      160: { icon: faShip, color: "text-blue-600", title: "Sea Freight" },
      510: { icon: faShip, color: "text-blue-600", title: "Sea Freight" },
      430: { icon: faShip, color: "text-blue-600", title: "Sea Freight" },

      // Roadfreight
      240: { icon: faTruck, color: "text-green-600", title: "Road Freight" },

      // Airfreight
      520: { icon: faPlane, color: "text-purple-600", title: "Air Freight" },
      521: { icon: faPlane, color: "text-purple-600", title: "Air Freight" },
      640: { icon: faPlane, color: "text-purple-600", title: "Air Freight" },

      // Default for unknown entry points
      default: {
        icon: faQuestion,
        color: "text-gray-500",
        title: "Unknown Transport Type",
      },
    };

    // Convert to number if it's a string
    const entryPointNum =
      typeof entryPoint === "string" ? parseInt(entryPoint, 10) : entryPoint;
    return transportIconsMap[entryPointNum] || transportIconsMap["default"];
  };

  const clearAllFilters = () => {
    setSelectedCustomers([]);
    setSelectedDivisions([]);
    setSelectedRfcs([]);
    setSelectedOperationTypes([]);
    setSelectedEntryPoints([]);
    setSelectedPedimentos([]);
    setSelectedStatus([]);
    setSearchTerm("");
    setDateRange([
      {
        startDate: null,
        endDate: null,
        key: "selection",
      },
    ]);
  };

  const clearFilterType = (filterType) => {
    switch (filterType) {
      case "customers":
        setSelectedCustomers([]);
        break;
      case "divisions":
        setSelectedDivisions([]);
        break;
      case "rfcs":
        setSelectedRfcs([]);
        break;
      case "operationTypes":
        setSelectedOperationTypes([]);
        break;
      case "entryPoints":
        setSelectedEntryPoints([]);
        break;
      case "pedimentos":
        setSelectedPedimentos([]);
        break;
      case "status":
        setSelectedStatus([]);
        break;
      case "search":
        setSearchTerm("");
        break;
      case "dateRange":
        setDateRange([
          {
            startDate: null,
            endDate: null,
            key: "selection",
          },
        ]);
        break;
      default:
        break;
    }
  };

  // Format date for display
  const formatDate = (date) => {
    if (!date) return "N/A";
    try {
      const dateObj = new Date(date);
      if (isNaN(dateObj.getTime())) return "N/A";

      const options = { year: "numeric", month: "short", day: "numeric" };
      return dateObj.toLocaleDateString(undefined, options);
    } catch (error) {
      console.error("Error formatting date:", error);
      return "N/A";
    }
  };

  // Get status color class based on status
  const getStatusColorClass = (status) => {
    if (status === "Pagado") return "bg-green-100 text-green-800";
    return "bg-yellow-100 text-yellow-800";
  };

  // Request sort by column
  const requestSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key) {
      direction = sortConfig.direction === "asc" ? "desc" : "asc";
    }
    setSortConfig({ key, direction });
  };

  // Check if any filters are active
  const hasActiveFilters =
    selectedCustomers.length > 0 ||
    selectedDivisions.length > 0 ||
    selectedRfcs.length > 0 ||
    selectedOperationTypes.length > 0 ||
    selectedEntryPoints.length > 0 ||
    selectedPedimentos.length > 0 ||
    selectedStatus.length > 0 ||
    (dateRange[0].startDate && dateRange[0].endDate) ||
    searchTerm.length > 0;

  // Handle chart click to show drill-down modal
  const handleChartItemClick = (chartId, itemData) => {
    // Set up drill-down configuration with context information
    const config = {
      chartType: itemData.type || "unknown",
      entryPoint: itemData.entryPoint && itemData.entryPoint !== "Unknown" ? itemData.entryPoint : null,
      clickedBracket: itemData.bracketType || null, // For context, but not auto-filtering
      count: itemData.count || 0,
    };

    setDrillDownConfig(config);
    setShowDrillDownModal(true);
  };

  // Handle drill-down modal close
  const handleCloseDrillDownModal = () => {
    setShowDrillDownModal(false);
    setDrillDownConfig(null);
  };

  // Handle page change in pagination
  const handlePageChange = (page) => {
    setPagination((prev) => ({
      ...prev,
      currentPage: page,
    }));
  };

  // Handle page size change
  const handlePageSizeChange = (size) => {
    setPagination((prev) => ({
      ...prev,
      pageSize: size,
      currentPage: 1, // Reset to first page when changing page size
    }));
  };

  // Prepare data for CSV export
  const transformDataForCSV = (data) => {
    return data.map((item) => {
      // Extract basic header fields
      const basicFields = {
        reference: item.header.reference,
        trailer_number: item.header.trailer_number,
        purchase_order: item.header.purchase_order,
        total_packages: item.header.total_packages,
        weight: item.header.weight,
        delivery_type: item.header.delivery_type,
        operation_type: getOperationTypeText(item.header.operation_type),
        customer_name: item.header.customer_name,
        customer_id: item.header.customer_id,
        customer_rfc: item.header.customer_rfc,
        customer_division: item.header.customer_division,
      };

      // Extract pedimento fields
      const pedimentoFields = {
        pedimento_license: item.header.pedimento.pedimento_license,
        pedimento_entry_point: item.header.pedimento.pedimento_entry_point,
        pedimento_number: item.header.pedimento.pedimento_number,
        pedimento_consolidation: item.header.pedimento.pedimento_consolidation,
        pedimento_remesa: item.header.pedimento.pedimento_remesa,
      };

      // Combine all sections into one flat object for CSV export
      return {
        ...basicFields,
        ...pedimentoFields,
      };
    });
  };

  // Toggle chart layout
  const toggleChartLayout = () => {
    setChartLayout((prev) => ({
      ...prev,
      columns: prev.columns === 2 ? 3 : 2, // Toggle between 2 and 3 columns
    }));
  };

  // Toggle chart size
  const toggleChartSize = () => {
    const sizes = ["small", "medium", "large"];
    const currentIndex = sizes.indexOf(chartLayout.size);
    const nextIndex = (currentIndex + 1) % sizes.length;

    setChartLayout((prev) => ({
      ...prev,
      size: sizes[nextIndex],
    }));
  };

  // Custom select styles for filters
  const customSelectStyles = {
    control: (base, state) => ({
      ...base,
      borderColor: state.isFocused ? "#3b82f6" : "#e2e8f0",
      boxShadow: state.isFocused ? "0 0 0 1px #3b82f6" : "none",
      "&:hover": {
        borderColor: "#cbd5e1",
      },
      borderRadius: "0.375rem",
      minHeight: "38px",
      transition: "all 0.2s ease",
    }),
    multiValue: (base) => ({
      ...base,
      backgroundColor: "#e1effe",
      borderRadius: "0.25rem",
    }),
    multiValueLabel: (base) => ({
      ...base,
      color: "#1e40af",
      fontWeight: 500,
      fontSize: "0.8125rem",
      padding: "0.125rem 0.25rem",
    }),
    multiValueRemove: (base) => ({
      ...base,
      color: "#60a5fa",
      ":hover": {
        backgroundColor: "#bfdbfe",
        color: "#1e3a8a",
      },
      borderRadius: "0 0.25rem 0.25rem 0",
    }),
    placeholder: (base) => ({
      ...base,
      color: "#94a3b8",
      fontSize: "0.875rem",
    }),
    menu: (base) => ({
      ...base,
      zIndex: 9999,
      borderRadius: "0.375rem",
      boxShadow:
        "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
      border: "1px solid #e2e8f0",
    }),
    option: (base, state) => ({
      ...base,
      backgroundColor: state.isSelected
        ? "#3b82f6"
        : state.isFocused
        ? "#e1effe"
        : "white",
      color: state.isSelected ? "white" : "#1e293b",
      fontSize: "0.875rem",
      padding: "0.5rem 0.75rem",
      ":active": {
        backgroundColor: state.isSelected ? "#3b82f6" : "#bfdbfe",
      },
    }),
  };

  // Determine chart size class based on layout settings
  const getChartSizeClass = () => {
    const columnClasses = {
      1: "grid-cols-1",
      2: "grid-cols-1 lg:grid-cols-2",
      3: "grid-cols-1 md:grid-cols-2 xl:grid-cols-3",
    };

    const sizeClasses = {
      small: "h-64",
      medium: "h-80",
      large: "h-96",
    };

    return {
      columns: columnClasses[chartLayout.columns],
      height: sizeClasses[chartLayout.size],
    };
  };

  // Calculate paginated data
  const paginatedData = (() => {
    // Sort data first
    const sortedData = [...filteredData].sort((a, b) => {
      const getNestedValue = (obj, path) =>
        path
          .split(".")
          .reduce((o, key) => (o && o[key] !== undefined ? o[key] : null), obj);

      let aVal = getNestedValue(a.header, sortConfig.key);
      let bVal = getNestedValue(b.header, sortConfig.key);

      // Handle date comparison
      if (sortConfig.key.includes("date") && aVal && bVal) {
        aVal = new Date(aVal);
        bVal = new Date(bVal);
      }

      if (aVal < bVal) return sortConfig.direction === "asc" ? -1 : 1;
      if (aVal > bVal) return sortConfig.direction === "asc" ? 1 : -1;
      return 0;
    });

    // Apply pagination
    const startIndex = (pagination.currentPage - 1) * pagination.pageSize;
    const endIndex = startIndex + pagination.pageSize;
    return sortedData.slice(startIndex, endIndex);
  })();

  // Render loading spinner
  if (isLoading && (!trafficData || trafficData.length === 0)) {
    return (
      <div className="flex justify-center items-center h-screen">
        <FontAwesomeIcon
          icon={faSpinner}
          className="text-3xl font-semibold animate-spin text-blue-600 mr-3"
        />
        <div className="text-2xl font-semibold">Loading Dashboard...</div>
      </div>
    );
  }

  // Get chart size styling
  const chartSizeClasses = getChartSizeClass();

  // Custom action buttons for Enhanced Dashboard
  const actionButtons = (
    <>
      <button
        onClick={handleRefreshData}
        className="flex items-center space-x-2 bg-green-600 hover:bg-green-700 px-4 py-2 rounded-lg text-sm font-medium transition-colors duration-200"
        disabled={isLoading}
      >
        <FontAwesomeIcon
          icon={isLoading ? faSpinner : faSync}
          className={isLoading ? "animate-spin" : ""}
        />
        <span>{isLoading ? "Refreshing..." : "Refresh Data"}</span>
      </button>
      <button
        onClick={toggleChartLayout}
        className="flex items-center space-x-2 bg-blue-600 hover:bg-blue-700 px-4 py-2 rounded-lg text-sm font-medium transition-colors duration-200"
      >
        <FontAwesomeIcon icon={faColumns} />
        <span>{`${chartLayout.columns} Columns`}</span>
      </button>
      <button
        onClick={toggleChartSize}
        className="flex items-center space-x-2 bg-blue-600 hover:bg-blue-700 px-4 py-2 rounded-lg text-sm font-medium transition-colors duration-200"
      >
        <FontAwesomeIcon
          icon={chartLayout.size === "large" ? faCompress : faExpand}
        />
        <span>{`${
          chartLayout.size.charAt(0).toUpperCase() +
          chartLayout.size.slice(1)
        } Size`}</span>
      </button>
    </>
  );

  return (
    <div className="bg-gray-50 min-h-screen flex flex-col">
      {/* GlobalHeader */}
      <GlobalHeader 
        title="Traffic Analytics Dashboard"
        icon={faChartBar}
        actionButtons={actionButtons}
      />
      
      {/* Tabs */}
      <div className="bg-gradient-to-r from-[#1A2654] to-blue-700">
        <div className="flex px-6 border-b border-blue-600">
          <button
            className={`px-4 py-2 font-medium text-sm rounded-t-lg transition-colors duration-200 ${
              activeTab === "overview"
                ? "bg-white text-blue-800"
                : "bg-transparent text-white hover:bg-blue-600"
            }`}
            onClick={() => setActiveTab("overview")}
          >
            <FontAwesomeIcon icon={faChartPie} className="mr-2" />
            Overview
          </button>
          <button
            className={`px-4 py-2 font-medium text-sm rounded-t-lg transition-colors duration-200 ${
              activeTab === "data"
                ? "bg-white text-blue-800"
                : "bg-transparent text-white hover:bg-blue-600"
            }`}
            onClick={() => setActiveTab("data")}
          >
            <FontAwesomeIcon icon={faTable} className="mr-2" />
            Data Grid
          </button>
        </div>
      </div>

      {/* Main content */}
      <div className="flex-1 p-6">
        {/* Search and Filter Bar */}
        <div className="mb-6">
          <div className="bg-white shadow-sm rounded-lg border border-gray-200">
            <div className="p-4">
              <div className="flex flex-col md:flex-row gap-3">
                {/* Search input */}
                <div className="w-full md:w-1/3">
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <FontAwesomeIcon
                        icon={faSearch}
                        className="text-gray-400"
                      />
                    </div>
                    <input
                      type="text"
                      className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg shadow-sm focus:ring-blue-500 focus:border-blue-500 transition-colors"
                      placeholder="Search shipments, references, customers..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                    {searchTerm && (
                      <button
                        onClick={() => setSearchTerm("")}
                        className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                      >
                        <FontAwesomeIcon icon={faTimes} />
                      </button>
                    )}
                  </div>
                </div>

                {/* Buttons group */}
                <div className="flex flex-wrap gap-2 justify-end ml-auto">
                  <button
                    onClick={() => setShowFilters(!showFilters)}
                    className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-all ${
                      showFilters
                        ? "bg-blue-100 text-blue-700 border border-blue-200"
                        : "bg-white border border-gray-300 text-gray-700 hover:bg-gray-50"
                    }`}
                  >
                    <FontAwesomeIcon icon={faFilter} />
                    <span>Filters</span>
                    <FontAwesomeIcon
                      icon={showFilters ? faChevronUp : faChevronDown}
                      className="text-xs"
                    />
                  </button>

                  <button
                    onClick={() => setShowDatePicker(!showDatePicker)}
                    className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-all ${
                      showDatePicker ||
                      (dateRange[0].startDate && dateRange[0].endDate)
                        ? "bg-blue-100 text-blue-700 border border-blue-200"
                        : "bg-white border border-gray-300 text-gray-700 hover:bg-gray-50"
                    }`}
                  >
                    <FontAwesomeIcon icon={faCalendarAlt} />
                    <span>Date Range</span>
                    {showDatePicker ? (
                      <FontAwesomeIcon icon={faChevronUp} className="text-xs" />
                    ) : (
                      <FontAwesomeIcon
                        icon={faChevronDown}
                        className="text-xs"
                      />
                    )}
                  </button>

                  <button
                    onClick={clearAllFilters}
                    disabled={!hasActiveFilters}
                    className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-all ${
                      hasActiveFilters
                        ? "bg-red-50 text-red-700 border border-red-200 hover:bg-red-100"
                        : "bg-gray-100 text-gray-400 border border-gray-200 cursor-not-allowed"
                    }`}
                  >
                    <FontAwesomeIcon icon={faTimes} />
                    <span>Clear All Filters</span>
                  </button>
                </div>
              </div>
            </div>

            {/* Date picker dropdown */}
            {showDatePicker && (
              <div className="border-t border-gray-200 bg-gray-50 p-4">
                <div className="flex justify-between items-center mb-3">
                  <h3 className="font-medium text-gray-700">
                    Select Date Range
                  </h3>
                  <div className="flex gap-2">
                    <button
                      onClick={() => clearFilterType("dateRange")}
                      className="text-sm text-gray-500 hover:text-red-600 transition-colors"
                    >
                      Clear Range
                    </button>
                    <button
                      onClick={() => setShowDatePicker(false)}
                      className="text-sm text-gray-500 hover:text-gray-800 transition-colors"
                    >
                      Close
                    </button>
                  </div>
                </div>

                <div className="flex justify-center">
                  <div className="border border-gray-300 rounded-lg shadow-md bg-white">
                    <DateRangePicker
                      ranges={dateRange}
                      onChange={(ranges) => setDateRange([ranges.selection])}
                      months={2}
                      direction="horizontal"
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Filter Panel */}
            {showFilters && (
              <div className="border-t border-gray-200 p-4 bg-gray-50">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
                  <div className="filter-group">
                    <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center">
                      <FontAwesomeIcon
                        icon={faBuilding}
                        className="mr-2 text-blue-600"
                      />
                      Customer
                    </label>
                    <Select
                      options={customerOptions}
                      isMulti
                      onChange={setSelectedCustomers}
                      value={selectedCustomers}
                      placeholder="Select Customers"
                      classNamePrefix="select-filter"
                      className="text-sm"
                      styles={customSelectStyles}
                    />
                  </div>

                  <div className="filter-group">
                    <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center">
                      <FontAwesomeIcon
                        icon={faBuilding}
                        className="mr-2 text-blue-600"
                      />
                      Division
                    </label>
                    <Select
                      options={divisionOptions}
                      isMulti
                      onChange={setSelectedDivisions}
                      value={selectedDivisions}
                      placeholder="Select Divisions"
                      classNamePrefix="select-filter"
                      className="text-sm"
                      styles={customSelectStyles}
                    />
                  </div>

                  <div className="filter-group">
                    <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center">
                      <FontAwesomeIcon
                        icon={faIdCard}
                        className="mr-2 text-blue-600"
                      />
                      RFC
                    </label>
                    <Select
                      options={rfcOptions}
                      isMulti
                      onChange={setSelectedRfcs}
                      value={selectedRfcs}
                      placeholder="Select RFCs"
                      classNamePrefix="select-filter"
                      className="text-sm"
                      styles={customSelectStyles}
                    />
                  </div>

                  <div className="filter-group">
                    <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center">
                      <FontAwesomeIcon
                        icon={faCog}
                        className="mr-2 text-blue-600"
                      />
                      Operation Type
                    </label>
                    <Select
                      options={operationTypeOptions}
                      isMulti
                      onChange={setSelectedOperationTypes}
                      value={selectedOperationTypes}
                      placeholder="Select Operation Types"
                      classNamePrefix="select-filter"
                      className="text-sm"
                      styles={customSelectStyles}
                    />
                  </div>

                  <div className="filter-group">
                    <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center">
                      <FontAwesomeIcon
                        icon={faMapMarkerAlt}
                        className="mr-2 text-blue-600"
                      />
                      Entry Point
                    </label>
                    <Select
                      options={entryPointOptions}
                      isMulti
                      onChange={setSelectedEntryPoints}
                      value={selectedEntryPoints}
                      placeholder="Select Entry Points"
                      classNamePrefix="select-filter"
                      className="text-sm"
                      styles={customSelectStyles}
                    />
                  </div>

                  <div className="filter-group">
                    <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center">
                      <FontAwesomeIcon
                        icon={faFileInvoice}
                        className="mr-2 text-blue-600"
                      />
                      Pedimento Number
                    </label>
                    <Select
                      options={pedimentoOptions}
                      isMulti
                      onChange={setSelectedPedimentos}
                      value={selectedPedimentos}
                      placeholder="Select Pedimento Numbers"
                      classNamePrefix="select-filter"
                      className="text-sm"
                      styles={customSelectStyles}
                    />
                  </div>

                  <div className="filter-group">
                    <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center">
                      <FontAwesomeIcon
                        icon={faClipboardCheck}
                        className="mr-2 text-blue-600"
                      />
                      Status
                    </label>
                    <Select
                      options={statusOptions}
                      isMulti
                      onChange={setSelectedStatus}
                      value={selectedStatus}
                      placeholder="Select Status"
                      classNamePrefix="select-filter"
                      className="text-sm"
                      styles={customSelectStyles}
                    />
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Active filters display */}
        {hasActiveFilters && (
          <div className="mb-6 flex flex-wrap gap-2">
            {selectedCustomers.map((item) => (
              <span
                key={item.value}
                className="bg-blue-50 text-blue-700 px-2 py-1 rounded-full flex items-center text-xs transition-all hover:bg-blue-100 border border-blue-200"
              >
                <span className="font-medium mr-1">Customer:</span> {item.label}
                <button
                  onClick={() => {
                    setSelectedCustomers((prev) =>
                      prev.filter((i) => i.value !== item.value)
                    );
                  }}
                  className="ml-1 text-blue-500 hover:text-blue-700 transition-colors"
                >
                  <FontAwesomeIcon icon={faTimes} className="text-xs" />
                </button>
              </span>
            ))}

            {selectedDivisions.map((item) => (
              <span
                key={item.value}
                className="bg-blue-50 text-blue-700 px-2 py-1 rounded-full flex items-center text-xs transition-all hover:bg-blue-100 border border-blue-200"
              >
                <span className="font-medium mr-1">Division:</span> {item.label}
                <button
                  onClick={() => {
                    setSelectedDivisions((prev) =>
                      prev.filter((i) => i.value !== item.value)
                    );
                  }}
                  className="ml-1 text-blue-500 hover:text-blue-700 transition-colors"
                >
                  <FontAwesomeIcon icon={faTimes} className="text-xs" />
                </button>
              </span>
            ))}

            {/* Continue with other filter tags */}

            {selectedRfcs.map((item) => (
              <span
                key={item.value}
                className="bg-blue-50 text-blue-700 px-2 py-1 rounded-full flex items-center text-xs transition-all hover:bg-blue-100 border border-blue-200"
              >
                <span className="font-medium mr-1">RFC:</span> {item.label}
                <button
                  onClick={() => {
                    setSelectedRfcs((prev) =>
                      prev.filter((i) => i.value !== item.value)
                    );
                  }}
                  className="ml-1 text-blue-500 hover:text-blue-700 transition-colors"
                >
                  <FontAwesomeIcon icon={faTimes} className="text-xs" />
                </button>
              </span>
            ))}

            {selectedOperationTypes.map((item) => (
              <span
                key={item.value}
                className="bg-blue-50 text-blue-700 px-2 py-1 rounded-full flex items-center text-xs transition-all hover:bg-blue-100 border border-blue-200"
              >
                <span className="font-medium mr-1">Op. Type:</span> {item.label}
                <button
                  onClick={() => {
                    setSelectedOperationTypes((prev) =>
                      prev.filter((i) => i.value !== item.value)
                    );
                  }}
                  className="ml-1 text-blue-500 hover:text-blue-700 transition-colors"
                >
                  <FontAwesomeIcon icon={faTimes} className="text-xs" />
                </button>
              </span>
            ))}

            {selectedEntryPoints.map((item) => (
              <span
                key={item.value}
                className="bg-blue-50 text-blue-700 px-2 py-1 rounded-full flex items-center text-xs transition-all hover:bg-blue-100 border border-blue-200"
              >
                <span className="font-medium mr-1">Entry Point:</span>{" "}
                {item.label}
                <button
                  onClick={() => {
                    setSelectedEntryPoints((prev) =>
                      prev.filter((i) => i.value !== item.value)
                    );
                  }}
                  className="ml-1 text-blue-500 hover:text-blue-700 transition-colors"
                >
                  <FontAwesomeIcon icon={faTimes} className="text-xs" />
                </button>
              </span>
            ))}

            {selectedPedimentos.map((item) => (
              <span
                key={item.value}
                className="bg-blue-50 text-blue-700 px-2 py-1 rounded-full flex items-center text-xs transition-all hover:bg-blue-100 border border-blue-200"
              >
                <span className="font-medium mr-1">Pedimento:</span>{" "}
                {item.label}
                <button
                  onClick={() => {
                    setSelectedPedimentos((prev) =>
                      prev.filter((i) => i.value !== item.value)
                    );
                  }}
                  className="ml-1 text-blue-500 hover:text-blue-700 transition-colors"
                >
                  <FontAwesomeIcon icon={faTimes} className="text-xs" />
                </button>
              </span>
            ))}

            {selectedStatus.map((item) => (
              <span
                key={item.value}
                className="bg-blue-50 text-blue-700 px-2 py-1 rounded-full flex items-center text-xs transition-all hover:bg-blue-100 border border-blue-200"
              >
                <span className="font-medium mr-1">Status:</span> {item.label}
                <button
                  onClick={() => {
                    setSelectedStatus((prev) =>
                      prev.filter((i) => i.value !== item.value)
                    );
                  }}
                  className="ml-1 text-blue-500 hover:text-blue-700 transition-colors"
                >
                  <FontAwesomeIcon icon={faTimes} className="text-xs" />
                </button>
              </span>
            ))}

            {searchTerm && (
              <span className="bg-blue-50 text-blue-700 px-2 py-1 rounded-full flex items-center text-xs transition-all hover:bg-blue-100 border border-blue-200">
                <span className="font-medium mr-1">Search:</span> {searchTerm}
                <button
                  onClick={() => setSearchTerm("")}
                  className="ml-1 text-blue-500 hover:text-blue-700 transition-colors"
                >
                  <FontAwesomeIcon icon={faTimes} className="text-xs" />
                </button>
              </span>
            )}

            {dateRange[0].startDate && dateRange[0].endDate && (
              <span className="bg-blue-50 text-blue-700 px-2 py-1 rounded-full flex items-center text-xs transition-all hover:bg-blue-100 border border-blue-200">
                <FontAwesomeIcon icon={faCalendarAlt} className="mr-1" />
                <span className="font-medium mr-1">Date Range:</span>
                {formatDate(dateRange[0].startDate)} -{" "}
                {formatDate(dateRange[0].endDate)}
                <button
                  onClick={() => clearFilterType("dateRange")}
                  className="ml-1 text-blue-500 hover:text-blue-700 transition-colors"
                >
                  <FontAwesomeIcon icon={faTimes} className="text-xs" />
                </button>
              </span>
            )}
          </div>
        )}

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-lg shadow p-4 border border-gray-200">
            <div className="flex items-start">
              <div className="bg-blue-100 p-3 rounded-full">
                <FontAwesomeIcon icon={faCubes} className="text-blue-600" />
              </div>
              <div className="ml-4">
                <h3 className="text-sm font-medium text-gray-500">
                  Total References
                </h3>
                <p className="text-2xl font-bold text-gray-900">
                  {filteredData.length}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-4 border border-gray-200">
            <div className="flex items-start">
              <div className="bg-green-100 p-3 rounded-full">
                <FontAwesomeIcon icon={faShip} className="text-green-600" />
              </div>
              <div className="ml-4">
                <h3 className="text-sm font-medium text-gray-500">
                  Sea Freight
                </h3>
                <p className="text-2xl font-bold text-gray-900">
                  {
                    filteredData.filter((item) =>
                      [160, 510, 430].includes(
                        parseInt(item.header.pedimento.pedimento_entry_point)
                      )
                    ).length
                  }
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-4 border border-gray-200">
            <div className="flex items-start">
              <div className="bg-purple-100 p-3 rounded-full">
                <FontAwesomeIcon icon={faPlane} className="text-purple-600" />
              </div>
              <div className="ml-4">
                <h3 className="text-sm font-medium text-gray-500">
                  Air Freight
                </h3>
                <p className="text-2xl font-bold text-gray-900">
                  {
                    filteredData.filter((item) =>
                      [520, 521, 640].includes(
                        parseInt(item.header.pedimento.pedimento_entry_point)
                      )
                    ).length
                  }
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-4 border border-gray-200">
            <div className="flex items-start">
              <div className="bg-orange-100 p-3 rounded-full">
                <FontAwesomeIcon icon={faTruck} className="text-orange-600" />
              </div>
              <div className="ml-4">
                <h3 className="text-sm font-medium text-gray-500">
                  Road Freight
                </h3>
                <p className="text-2xl font-bold text-gray-900">
                  {
                    filteredData.filter((item) =>
                      [240].includes(
                        parseInt(item.header.pedimento.pedimento_entry_point)
                      )
                    ).length
                  }
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Chart layout or data grid based on active tab */}
        {activeTab === "overview" ? (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
            {selectedCharts.map((chartId) => {
              const chart = availableCharts.find((c) => c.id === chartId);
              if (!chart) return null;

              const ChartComponent = chart.component;
              return (
                <div
                  key={chartId}
                  className="bg-white shadow rounded-lg p-4 border border-gray-200 flex flex-col"
                  style={{
                    height:
                      chartLayout.size === "small"
                        ? "320px"
                        : chartLayout.size === "medium"
                        ? "400px"
                        : "500px",
                  }}
                >
                  <div className="mb-3 flex items-center justify-between">
                    <h3 className="text-lg font-semibold text-gray-800 flex items-center">
                      <FontAwesomeIcon
                        icon={chart.icon}
                        className="text-blue-600 mr-2"
                      />
                      {chart.title}
                    </h3>
                    <button
                      className="text-sm text-blue-600 hover:text-blue-700 flex items-center"
                      onClick={() => {
                        setSelectedChart(chartId);
                        setActiveTab("data");
                      }}
                    >
                      View Details{" "}
                      <FontAwesomeIcon icon={faArrowRight} className="ml-1" />
                    </button>
                  </div>
                  <div className="flex-grow overflow-hidden">
                    <div className="h-full">
                      <ChartComponent
                        data={filteredData}
                        isLoading={isLoading}
                        onChartItemClick={(item) =>
                          handleChartItemClick(chartId, item)
                        }
                      />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          // Data Grid Tab
          <div className="bg-white shadow-lg rounded-lg border border-gray-200 overflow-hidden p-0">
            <div className="p-4 bg-gray-50 border-b border-gray-200 flex justify-between items-center">
              <h3 className="text-lg font-semibold text-gray-800 flex items-center">
                <FontAwesomeIcon
                  icon={faTable}
                  className="text-blue-600 mr-2"
                />
                Traffic Data Grid
              </h3>

              <div className="flex items-center space-x-3">
                <span className="text-sm text-gray-500">
                  Showing {paginatedData.length} of {filteredData.length}{" "}
                  records
                </span>

                <button className="flex items-center space-x-2 bg-green-50 text-green-700 border border-green-200 px-3 py-1.5 rounded-lg text-sm font-medium hover:bg-green-100 transition-colors">
                  <FontAwesomeIcon icon={faFileExcel} />
                  <span>Export to CSV</span>
                </button>
              </div>
            </div>

            {/* Data Grid */}
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th
                      scope="col"
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                    >
                      <button
                        className="flex items-center space-x-1 hover:text-blue-700 focus:outline-none"
                        onClick={() => requestSort("reference")}
                      >
                        <span>Reference</span>
                        {sortConfig.key === "reference" && (
                          <FontAwesomeIcon
                            icon={
                              sortConfig.direction === "asc"
                                ? faChevronUp
                                : faChevronDown
                            }
                            className="ml-1 text-blue-500"
                            size="xs"
                          />
                        )}
                      </button>
                    </th>
                    <th
                      scope="col"
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                    >
                      <button
                        className="flex items-center space-x-1 hover:text-blue-700 focus:outline-none"
                        onClick={() =>
                          requestSort("pedimento.pedimento_entry_point")
                        }
                      >
                        <span>Entry Point</span>
                        {sortConfig.key ===
                          "pedimento.pedimento_entry_point" && (
                          <FontAwesomeIcon
                            icon={
                              sortConfig.direction === "asc"
                                ? faChevronUp
                                : faChevronDown
                            }
                            className="ml-1 text-blue-500"
                            size="xs"
                          />
                        )}
                      </button>
                    </th>
                    <th
                      scope="col"
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                    >
                      <button
                        className="flex items-center space-x-1 hover:text-blue-700 focus:outline-none"
                        onClick={() =>
                          requestSort("pedimento.pedimento_number")
                        }
                      >
                        <span>Pedimento #</span>
                        {sortConfig.key === "pedimento.pedimento_number" && (
                          <FontAwesomeIcon
                            icon={
                              sortConfig.direction === "asc"
                                ? faChevronUp
                                : faChevronDown
                            }
                            className="ml-1 text-blue-500"
                            size="xs"
                          />
                        )}
                      </button>
                    </th>
                    <th
                      scope="col"
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                    >
                      <button
                        className="flex items-center space-x-1 hover:text-blue-700 focus:outline-none"
                        onClick={() => requestSort("operation_type")}
                      >
                        <span>Operation</span>
                        {sortConfig.key === "operation_type" && (
                          <FontAwesomeIcon
                            icon={
                              sortConfig.direction === "asc"
                                ? faChevronUp
                                : faChevronDown
                            }
                            className="ml-1 text-blue-500"
                            size="xs"
                          />
                        )}
                      </button>
                    </th>
                    <th
                      scope="col"
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                    >
                      <button
                        className="flex items-center space-x-1 hover:text-blue-700 focus:outline-none"
                        onClick={() => requestSort("customer_name")}
                      >
                        <span>Customer</span>
                        {sortConfig.key === "customer_name" && (
                          <FontAwesomeIcon
                            icon={
                              sortConfig.direction === "asc"
                                ? faChevronUp
                                : faChevronDown
                            }
                            className="ml-1 text-blue-500"
                            size="xs"
                          />
                        )}
                      </button>
                    </th>
                    <th
                      scope="col"
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                    >
                      <button
                        className="flex items-center space-x-1 hover:text-blue-700 focus:outline-none"
                        onClick={() =>
                          requestSort("detail.dates_data.date_init")
                        }
                      >
                        <span>Created</span>
                        {sortConfig.key === "detail.dates_data.date_init" && (
                          <FontAwesomeIcon
                            icon={
                              sortConfig.direction === "asc"
                                ? faChevronUp
                                : faChevronDown
                            }
                            className="ml-1 text-blue-500"
                            size="xs"
                          />
                        )}
                      </button>
                    </th>
                    <th
                      scope="col"
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                    >
                      <button
                        className="flex items-center space-x-1 hover:text-blue-700 focus:outline-none"
                        onClick={() =>
                          requestSort("detail.traffic_data.status")
                        }
                      >
                        <span>Status</span>
                        {sortConfig.key === "detail.traffic_data.status" && (
                          <FontAwesomeIcon
                            icon={
                              sortConfig.direction === "asc"
                                ? faChevronUp
                                : faChevronDown
                            }
                            className="ml-1 text-blue-500"
                            size="xs"
                          />
                        )}
                      </button>
                    </th>
                    <th
                      scope="col"
                      className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider"
                    >
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {paginatedData.length === 0 ? (
                    <tr>
                      <td
                        colSpan="8"
                        className="px-6 py-8 text-center text-gray-500"
                      >
                        No traffic data found matching your criteria
                      </td>
                    </tr>
                  ) : (
                    paginatedData.map((item, index) => (
                      <tr
                        key={`${item.header.reference}-${index}`}
                        className="hover:bg-gray-50"
                      >
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          <div className="font-medium">
                            {item.header.reference}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          <div className="flex items-center">
                            {item.header.pedimento && (
                              <>
                                <FontAwesomeIcon
                                  icon={
                                    getTransportIcon(
                                      item.header.pedimento
                                        .pedimento_entry_point
                                    ).icon
                                  }
                                  className={`${
                                    getTransportIcon(
                                      item.header.pedimento
                                        .pedimento_entry_point
                                    ).color
                                  } mr-2`}
                                  title={
                                    getTransportIcon(
                                      item.header.pedimento
                                        .pedimento_entry_point
                                    ).title
                                  }
                                />
                                <span>
                                  {item.header.pedimento.pedimento_entry_point}
                                </span>
                              </>
                            )}
                            {!item.header.pedimento && (
                              <span className="text-gray-400">N/A</span>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {item.header.pedimento.pedimento_number || "N/A"}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {getOperationTypeText(item.header.operation_type)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          <div className="font-medium">
                            {item.header.customer_name}
                          </div>
                          <div className="text-xs text-gray-500 mt-1">
                            {item.header.customer_division}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {formatDate(
                            item.header.detail?.dates_data?.date_init
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {item.header.detail?.traffic_data?.status ? (
                            <div
                              className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColorClass(
                                item.header.detail?.traffic_data?.status
                              )}`}
                            >
                              {item.header.detail?.traffic_data?.status ===
                              "Pagado"
                                ? "Paid"
                                : "Open"}
                            </div>
                          ) : (
                            <span className="text-gray-400">N/A</span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm">
                          <button
                            onClick={() => {
                              setDetailsItem(item);
                              setShowDetailsPanel(true);
                            }}
                            className="text-blue-600 hover:text-blue-800 transition-colors font-medium"
                          >
                            View Details
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex items-center justify-between">
              <div className="flex items-center">
                <select
                  className="mr-2 border-gray-300 rounded-md shadow-sm text-sm focus:ring-blue-500 focus:border-blue-500"
                  value={pagination.pageSize}
                  onChange={(e) => handlePageSizeChange(Number(e.target.value))}
                >
                  {[10, 25, 50, 100].map((size) => (
                    <option key={size} value={size}>
                      {size} per page
                    </option>
                  ))}
                </select>
                <span className="text-sm text-gray-500">
                  Showing{" "}
                  {filteredData.length > 0
                    ? (pagination.currentPage - 1) * pagination.pageSize + 1
                    : 0}{" "}
                  to{" "}
                  {Math.min(
                    pagination.currentPage * pagination.pageSize,
                    filteredData.length
                  )}{" "}
                  of {filteredData.length} records
                </span>
              </div>

              <div className="flex items-center space-x-2">
                <button
                  onClick={() => handlePageChange(1)}
                  disabled={
                    pagination.currentPage === 1 || pagination.totalPages === 0
                  }
                  className={`px-2 py-1 text-sm rounded ${
                    pagination.currentPage === 1 || pagination.totalPages === 0
                      ? "text-gray-400 cursor-not-allowed"
                      : "text-blue-600 hover:bg-blue-50"
                  }`}
                >
                  First
                </button>

                <button
                  onClick={() => handlePageChange(pagination.currentPage - 1)}
                  disabled={
                    pagination.currentPage === 1 || pagination.totalPages === 0
                  }
                  className={`px-2 py-1 text-sm rounded ${
                    pagination.currentPage === 1 || pagination.totalPages === 0
                      ? "text-gray-400 cursor-not-allowed"
                      : "text-blue-600 hover:bg-blue-50"
                  }`}
                >
                  Previous
                </button>

                {/* Page numbers */}
                <div className="flex items-center">
                  {Array.from(
                    { length: Math.min(5, pagination.totalPages) },
                    (_, i) => {
                      // Create a window of pages centered around current page
                      let pageWindow = [];
                      if (pagination.totalPages <= 5) {
                        // Show all pages if 5 or fewer
                        pageWindow = Array.from(
                          { length: pagination.totalPages },
                          (_, i) => i + 1
                        );
                      } else {
                        // Create window around current page
                        const start = Math.max(
                          1,
                          Math.min(
                            pagination.currentPage - 2,
                            pagination.totalPages - 4
                          )
                        );
                        pageWindow = Array.from(
                          { length: 5 },
                          (_, i) => start + i
                        );
                      }

                      return pageWindow.map((pageNum) => (
                        <button
                          key={pageNum}
                          onClick={() => handlePageChange(pageNum)}
                          className={`w-8 h-8 flex items-center justify-center rounded-md mx-0.5 ${
                            pagination.currentPage === pageNum
                              ? "bg-blue-600 text-white"
                              : "text-gray-700 hover:bg-gray-100"
                          }`}
                        >
                          {pageNum}
                        </button>
                      ));
                    }
                  )}
                </div>

                <button
                  onClick={() => handlePageChange(pagination.currentPage + 1)}
                  disabled={pagination.currentPage === pagination.totalPages}
                  className={`px-2 py-1 text-sm rounded ${
                    pagination.currentPage === pagination.totalPages
                      ? "text-gray-400 cursor-not-allowed"
                      : "text-blue-600 hover:bg-blue-50"
                  }`}
                >
                  Next
                </button>

                <button
                  onClick={() => handlePageChange(pagination.totalPages)}
                  disabled={pagination.currentPage === pagination.totalPages}
                  className={`px-2 py-1 text-sm rounded ${
                    pagination.currentPage === pagination.totalPages
                      ? "text-gray-400 cursor-not-allowed"
                      : "text-blue-600 hover:bg-blue-50"
                  }`}
                >
                  Last
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Details Panel (Slide In from right) */}
        {detailsItem && (
          <div
            className="fixed right-0 top-0 bottom-0 z-40 shadow-xl border-l border-gray-200 max-h-full flex flex-col bg-white transition-transform duration-300 ease-in-out"
            style={{
              width: "30rem",
              transform: showDetailsPanel
                ? "translateX(0)"
                : "translateX(100%)",
            }}
          >
            <div className="p-4 bg-gradient-to-r from-[#1A2654] to-blue-700 text-white flex justify-between items-center">
              <h3 className="text-lg font-medium">Reference Details</h3>
              <button
                onClick={() => setShowDetailsPanel(false)}
                className="text-white hover:text-gray-200 transition-colors"
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-5">
              <div className="space-y-6">
                {/* Check if this is chart data or traffic item data */}
                {detailsItem.type ? (
                  /* Chart Data Information */
                  <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                    <h4 className="text-sm font-medium text-gray-900 mb-3 pb-2 border-b flex items-center">
                      <FontAwesomeIcon
                        icon={faChartBar}
                        className="mr-2 text-blue-600"
                      />
                      Chart Information
                    </h4>
                    <div className="grid grid-cols-2 gap-3 text-sm">
                      <div>
                        <span className="text-gray-500">Chart Type:</span>
                        <p className="font-medium capitalize">
                          {detailsItem.type}
                        </p>
                      </div>
                      {detailsItem.entryPoint && (
                        <div>
                          <span className="text-gray-500">Entry Point:</span>
                          <p className="font-medium">
                            {detailsItem.entryPoint}
                          </p>
                        </div>
                      )}
                      {detailsItem.bracketType && (
                        <div>
                          <span className="text-gray-500">Performance Category:</span>
                          <p className="font-medium capitalize">
                            {detailsItem.bracketType}
                          </p>
                        </div>
                      )}
                      {detailsItem.count && (
                        <div>
                          <span className="text-gray-500">Count:</span>
                          <p className="font-medium">
                            {detailsItem.count} references
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                ) : (
                  /* Traffic Item Information */
                  <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                    <h4 className="text-sm font-medium text-gray-900 mb-3 pb-2 border-b flex items-center">
                      <FontAwesomeIcon
                        icon={faInfoCircle}
                        className="mr-2 text-blue-600"
                      />
                      General Information
                    </h4>
                    <div className="grid grid-cols-2 gap-3 text-sm">
                      <div>
                        <span className="text-gray-500">Reference:</span>
                        <p className="font-medium">
                          {detailsItem.header?.reference || "N/A"}
                        </p>
                      </div>
                      <div>
                        <span className="text-gray-500">Operation Type:</span>
                        <p className="font-medium">
                          {getOperationTypeText(
                            detailsItem.header?.operation_type
                          )}
                        </p>
                      </div>
                      <div>
                        <span className="text-gray-500">Status:</span>
                        <div
                          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColorClass(
                            detailsItem.header?.detail?.traffic_data?.status
                          )}`}
                        >
                          {detailsItem.header?.detail?.traffic_data?.status ===
                          "Pagado"
                            ? "Paid"
                            : "Open"}
                        </div>
                      </div>
                      <div>
                        <span className="text-gray-500">Purchase Order:</span>
                        <p className="font-medium">
                          {detailsItem.header?.purchase_order || "N/A"}
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Only show detailed sections for traffic item data, not chart data */}
                {!detailsItem.type && (
                  <>
                    {/* Customer Information */}
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                  <h4 className="text-sm font-medium text-gray-900 mb-3 pb-2 border-b flex items-center">
                    <FontAwesomeIcon
                      icon={faBuilding}
                      className="mr-2 text-blue-600"
                    />
                    Customer Information
                  </h4>
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div>
                      <span className="text-gray-500">Customer Name:</span>
                      <p className="font-medium">
                        {detailsItem.header?.customer_name}
                      </p>
                    </div>
                    <div>
                      <span className="text-gray-500">Customer ID:</span>
                      <p className="font-medium">
                        {detailsItem.header?.customer_id}
                      </p>
                    </div>
                    <div>
                      <span className="text-gray-500">Customer RFC:</span>
                      <p className="font-medium">
                        {detailsItem.header?.customer_rfc}
                      </p>
                    </div>
                    <div>
                      <span className="text-gray-500">Division:</span>
                      <p className="font-medium">
                        {detailsItem.header?.customer_division || "N/A"}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Pedimento Information */}
                {detailsItem.header?.pedimento && (
                  <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                    <h4 className="text-sm font-medium text-gray-900 mb-3 pb-2 border-b flex items-center">
                      <FontAwesomeIcon
                        icon={faFileInvoice}
                        className="mr-2 text-blue-600"
                      />
                      Pedimento Information
                    </h4>
                    <div className="grid grid-cols-2 gap-3 text-sm">
                      <div>
                        <span className="text-gray-500">Pedimento Number:</span>
                        <p className="font-medium">
                          {detailsItem.header?.pedimento?.pedimento_number ||
                            "N/A"}
                        </p>
                      </div>
                      <div>
                        <span className="text-gray-500">Entry Point:</span>
                        <p className="font-medium flex items-center">
                          <FontAwesomeIcon
                            icon={
                              getTransportIcon(
                                detailsItem.header?.pedimento
                                  ?.pedimento_entry_point
                              ).icon
                            }
                            className={`${
                              getTransportIcon(
                                detailsItem.header?.pedimento
                                  ?.pedimento_entry_point
                              ).color
                            } mr-1`}
                          />
                          {detailsItem.header?.pedimento?.pedimento_entry_point}
                        </p>
                      </div>
                      <div>
                        <span className="text-gray-500">License:</span>
                        <p className="font-medium">
                          {detailsItem.header?.pedimento?.pedimento_license ||
                            "N/A"}
                        </p>
                      </div>
                      <div>
                        <span className="text-gray-500">Consolidation:</span>
                        <p className="font-medium">
                          {detailsItem.header?.pedimento
                            .pedimento_consolidation || "N/A"}
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Transport Information */}
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                  <h4 className="text-sm font-medium text-gray-900 mb-3 pb-2 border-b flex items-center">
                    <FontAwesomeIcon
                      icon={faTruck}
                      className="mr-2 text-blue-600"
                    />
                    Transport Information
                  </h4>
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div>
                      <span className="text-gray-500">Trailer Number:</span>
                      <p className="font-medium">
                        {detailsItem.header?.trailer_number || "N/A"}
                      </p>
                    </div>
                    <div>
                      <span className="text-gray-500">Total Packages:</span>
                      <p className="font-medium">
                        {detailsItem.header?.total_packages || "N/A"}
                      </p>
                    </div>
                    <div>
                      <span className="text-gray-500">Weight:</span>
                      <p className="font-medium">
                        {detailsItem.header?.weight || "N/A"}
                      </p>
                    </div>
                    <div>
                      <span className="text-gray-500">Delivery Type:</span>
                      <p className="font-medium">
                        {detailsItem.header?.delivery_type || "N/A"}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Dates Information */}
                {detailsItem.header.detail?.dates_data && (
                  <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                    <h4 className="text-sm font-medium text-gray-900 mb-3 pb-2 border-b flex items-center">
                      <FontAwesomeIcon
                        icon={faCalendarAlt}
                        className="mr-2 text-blue-600"
                      />
                      Dates Information
                    </h4>
                    <div className="grid grid-cols-2 gap-3 text-sm">
                      {detailsItem.header?.detail?.dates_data.date_init && (
                        <div>
                          <span className="text-gray-500">Created Date:</span>
                          <p className="font-medium">
                            {formatDate(
                              detailsItem.header?.detail?.dates_data.date_init
                            )}
                          </p>
                        </div>
                      )}
                      {detailsItem.header?.detail?.dates_data.date_inbound && (
                        <div>
                          <span className="text-gray-500">Inbound Date:</span>
                          <p className="font-medium">
                            {formatDate(
                              detailsItem.header?.detail?.dates_data.date_inbound
                            )}
                          </p>
                        </div>
                      )}
                      {detailsItem.header?.detail?.dates_data.date_end && (
                        <div>
                          <span className="text-gray-500">End Date:</span>
                          <p className="font-medium">
                            {formatDate(
                              detailsItem.header?.detail?.dates_data.date_end
                            )}
                          </p>
                        </div>
                      )}
                      {detailsItem.header?.detail?.dates_data.date_crossing && (
                        <div>
                          <span className="text-gray-500">Crossing Date:</span>
                          <p className="font-medium">
                            {formatDate(
                              detailsItem.header?.detail?.dates_data.date_crossing
                            )}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                )}
                  </>
                )}

                {/* Actions */}
                <div className="flex justify-end space-x-3">
                  <button
                    onClick={() => setShowDetailsPanel(false)}
                    className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors text-sm font-medium"
                  >
                    Close
                  </button>
                  <button className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors text-sm font-medium">
                    <FontAwesomeIcon icon={faDownload} className="mr-1" />
                    Export Details
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Overlay when details panel is open */}
        {showDetailsPanel && (
          <div
            className="fixed inset-0 bg-black bg-opacity-30 z-30"
            onClick={() => setShowDetailsPanel(false)}
          ></div>
        )}

        {/* Drill Down Modal */}
        <DrillDownModal
          isOpen={showDrillDownModal}
          onClose={handleCloseDrillDownModal}
          data={trafficData}
          filterConfig={drillDownConfig}
        />
      </div>
    </div>
  );
};

export default EnhancedDashboard;
